// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useUser, useAuth } from '@/auth/clerkShim';
import { useOrganizationList } from '@/auth/clerkShim';
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import _ from 'lodash';
import CreateOrganizationModal from "@/components/CreateOrganization/CreateOrganizationModal";

interface LeagueSelectorProps {
  league: any;
}

// Capitalize the first letter of every word while preserving the rest of each
// word as-is (so acronyms like "NBA" and names like "Men's" survive). Unlike
// _.capitalize, which uppercases only the first char and lowercases everything
// after it.
const toTitleCase = (str?: string) =>
  (str || '').replace(/(^|\s)\S/g, (t) => t.toUpperCase());

export default function LeagueSelector({ league }: LeagueSelectorProps) {
  const { isSignedIn, user } = useUser();
  const [memberships, setMemberships] = useState([]);
  const { setActive } = useOrganizationList();
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isSignedIn) {
      async_getOrganizationMemberships();
    }
  }, [isSignedIn]);

  const async_getOrganizationMemberships = async () => {
    const memberships = await user.getOrganizationMemberships();
    setMemberships(memberships);
  };

  const onOrganizationChange = async (e) => {
    const organizationId = e.target.value;
    await setActive({ organization: organizationId });
    window.location.href = '/admin/dashboard';
  };

  const getAdminRole = () => {
    const membership = _.find(_.get(user, 'organizationMemberships', []), (membership) => membership.organization.id === auth.orgId);
    return _.get(membership, 'role', 'org:member');
  };

  const isAdmin = getAdminRole() === 'org:admin';

  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const selectedLeague = _.find(memberships, (membership) => membership.organization.id === auth.orgId)?.organization || { name: league?.displayName || 'Select League' };

  return (
    <div className="inline-block w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" role="combobox" aria-expanded={open} className="w-full pl-4 pr-2 justify-between h-12 bg-card hover:bg-muted/50 border border-border text-foreground shadow-sm">
            <div className="flex items-center text-base font-medium truncate">{toTitleCase(selectedLeague.name)}</div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-64" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>No league found.</CommandEmpty>
              <CommandGroup>
                {_.map(memberships, (membership) => (
                  <CommandItem
                    key={membership.organization.id}
                    value={membership.organization.id}
                    onSelect={() => {
                      onOrganizationChange({ target: { value: membership.organization.id } });
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <span className="font-medium">{toTitleCase(membership.organization.name)}</span>
                    <Check
                      className={cn("ml-auto h-4 w-4", auth.orgId === membership.organization.id ? "opacity-100" : "opacity-0")}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    if (!isAdmin) return;
                    setOpen(false);
                    setCreateOpen(true);
                  }}
                  className={cn(
                    "flex items-center gap-2 cursor-pointer",
                    !isAdmin && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={!isAdmin}
                >
                  <Plus className="h-4 w-4" />
                  <span className="font-medium">Create Organization</span>
                  {!isAdmin && (
                    <span className="ml-auto text-xs text-muted-foreground">Admin only</span>
                  )}
                </CommandItem>
              </CommandGroup>
            </CommandList>
            <div className="border-t px-3 py-2">
              <p className="text-xs text-muted-foreground text-center">Powered by Staty</p>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
      <CreateOrganizationModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        league={league}
      />
    </div>
  );
}